import {
  CHEQUE_SORT_FIELDS,
  type ChequeDirection,
  type ChequeStage,
  chequeStageSchema,
  chequeStatusSchema,
} from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'
import { PAGE_SIZES } from '../sales/search'

/**
 * The cheque register lives in the URL, like the other lists: the side (received or issued), the view
 * (what waits, by default), the stage, the filters and the page. An invalid value falls back to its
 * default instead of breaking the page.
 */
type PageSize = (typeof PAGE_SIZES)[number]
type SortField = (typeof CHEQUE_SORT_FIELDS)[number]
export type ChequeSort = SortField | `-${SortField}`

export function isChequeSort(value: string): value is ChequeSort {
  return (CHEQUE_SORT_FIELDS as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
}

/** Soonest first: what is due comes to the top. */
export const CHEQUE_DEFAULT_SORT = 'chequeDate'

/** The register's views: open (what waits on someone, the default), cleared, bounced, closed, all. */
export const CHEQUE_VIEWS = ['open', 'cleared', 'bounced', 'closed', 'all'] as const
export type ChequeView = (typeof CHEQUE_VIEWS)[number]

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)
const uuid = z.uuid().optional().catch(undefined)

export const chequeSearchSchema = z.object({
  direction: z.enum(['RECEIVED', 'ISSUED']).optional().catch(undefined),
  view: z.enum(CHEQUE_VIEWS).optional().catch(undefined),
  /** A figure of the register (due now, this week, post-dated, at the bank, late to clear). */
  stage: chequeStageSchema.optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is PageSize => (PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
  sort: z.string().refine(isChequeSort).optional().catch(undefined),
  q: z.string().trim().max(200).optional().catch(undefined),
  /** The account it is deposited into (received) or drawn on (issued). */
  account: uuid,
  customerId: uuid,
  supplierId: uuid,
  from: isoDate,
  to: isoDate,
})
export type ChequeSearch = z.infer<typeof chequeSearchSchema>

export interface ChequeListParams {
  direction: ChequeDirection
  page: number
  pageSize: number
  sort: string
  status?: z.infer<typeof chequeStatusSchema> | 'open' | undefined
  stage?: ChequeStage | undefined
  q?: string | undefined
  treasuryAccountId?: string | undefined
  customerId?: string | undefined
  supplierId?: string | undefined
  from?: string | undefined
  to?: string | undefined
}

const VIEW_STATUS: Record<ChequeView, ChequeListParams['status']> = {
  open: 'open',
  cleared: 'CLEARED',
  bounced: 'BOUNCED',
  // Handed back (received) or cancelled (issued).
  closed: undefined,
  all: undefined,
}

/** The list query for the side the page shows (`direction`, chosen from what the user may read). */
export function toChequeListQuery(search: ChequeSearch, direction: ChequeDirection): ChequeListParams {
  const view = search.view ?? 'open'
  return {
    direction,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? CHEQUE_DEFAULT_SORT,
    status: view === 'closed' ? (direction === 'RECEIVED' ? 'RETURNED' : 'CANCELLED') : VIEW_STATUS[view],
    // A stage speaks of open cheques only.
    stage: view === 'open' ? search.stage : undefined,
    q: search.q || undefined,
    treasuryAccountId: search.account,
    customerId: direction === 'RECEIVED' ? search.customerId : undefined,
    supplierId: direction === 'ISSUED' ? search.supplierId : undefined,
    from: search.from,
    to: search.to,
  }
}
